import { ReactNode } from 'react'
import { ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { cn } from '@/utils/cn'

interface Step {
  id: string
  title: string
  description?: string
  component: ReactNode
  isComplete?: boolean
  isValid?: boolean
}

interface QuestionnaireWizardProps {
  steps: Step[]
  currentStep: number
  onStepChange: (step: number) => void
  onComplete: () => void
  isLoading?: boolean
  className?: string
}

export function QuestionnaireWizard({
  steps,
  currentStep,
  onStepChange,
  onComplete,
  isLoading = false,
  className
}: QuestionnaireWizardProps) {
  const currentStepData = steps[currentStep]

  const handlePrevious = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1)
    }
  }

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      onStepChange(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const canProceed = currentStepData.isValid !== false

  return (
    <div className={cn("w-full max-w-4xl mx-auto", className)}>
      {/* Progress Bar */}
      <div className="mb-8 px-4">
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-start justify-between w-full max-w-2xl mx-auto">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center justify-start flex-1">
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200",
                    index < currentStep
                      ? "bg-brand-primary text-white"
                      : index === currentStep
                        ? "bg-brand-primary text-white ring-4 ring-brand-primary/20"
                        : "bg-brand-surface-alt text-brand-text-muted"
                  )}
                >
                  {index < currentStep ? (
                    <Check size={14} />
                  ) : (
                    index + 1
                  )}
                </div>
                <div className="mt-1 text-center hidden sm:block">
                  <div
                    className={cn(
                      "text-xs font-medium max-w-20 sm:max-w-24 text-center leading-tight",
                      index <= currentStep
                        ? "text-brand-text"
                        : "text-brand-text-muted"
                    )}
                  >
                    {step.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Progress Dots */}
        <div className="sm:hidden flex justify-center space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                index <= currentStep
                  ? "bg-brand-primary"
                  : "bg-brand-border"
              )}
            />
          ))}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="card p-6 mb-8">
        <div className="mb-6 text-center">
          <h2 className="text-xl font-semibold text-brand-text mb-2 text-center">
            {currentStepData.title}
          </h2>
          {currentStepData.description && (
            <p className="text-brand-text-secondary text-center">
              {currentStepData.description}
            </p>
          )}
        </div>

        <div className="min-h-[400px]">
          {currentStepData.component}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handlePrevious}
          disabled={currentStep === 0 || isLoading}
          className={cn(
            "btn-secondary flex items-center gap-2",
            currentStep === 0 && "invisible"
          )}
        >
          <ChevronLeft size={18} />
          <span className="hidden sm:inline">Anterior</span>
        </button>

        <div className="text-sm text-brand-text-secondary">
          Passo {currentStep + 1} de {steps.length}
        </div>

        {/* Botão de navegação - não mostrar no último passo */}
        {currentStep < steps.length - 1 && (
          <button
            onClick={handleNext}
            disabled={!canProceed || isLoading}
            className={cn(
              "btn-primary flex items-center gap-2",
              (!canProceed || isLoading) && "opacity-50 cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span className="hidden sm:inline">Processando...</span>
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight size={18} />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
